import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPackage,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiCheckCircle,
  FiTruck,
  FiXCircle,
  FiShoppingBag,
} from 'react-icons/fi';
import { ecommerceService } from '../services';
import { LoaderComponent, EmptyState } from '../components/ui';
import type { Order, OrderState } from '../types';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency: 'CVE',
    minimumFractionDigits: 0,
  }).format(value);

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-CV', {
      timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

type TabFilter = 'TODAS' | OrderState;

interface TabOption {
  key: TabFilter;
  label: string;
}

const TABS: TabOption[] = [
  { key: 'TODAS', label: 'Todas' },
  { key: 'PENDENTE', label: 'Pendentes' },
  { key: 'EM_PREPARACAO', label: 'Em Preparação' },
  { key: 'ENVIADO', label: 'Enviadas' },
  { key: 'ENTREGUE', label: 'Entregues' },
];

const STATUS_CONFIG: Record<
  OrderState,
  { label: string; bgColor: string; textColor: string; icon: React.ReactNode }
> = {
  PENDENTE: {
    label: 'Pendente',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    icon: <FiClock className="text-xs" />,
  },
  CONFIRMADO: {
    label: 'Confirmado',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    icon: <FiCheckCircle className="text-xs" />,
  },
  EM_PREPARACAO: {
    label: 'Em Preparação',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    icon: <FiPackage className="text-xs" />,
  },
  ENVIADO: {
    label: 'Enviado',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    icon: <FiTruck className="text-xs" />,
  },
  ENTREGUE: {
    label: 'Entregue',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    icon: <FiCheckCircle className="text-xs" />,
  },
  CANCELADO: {
    label: 'Cancelado',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    icon: <FiXCircle className="text-xs" />,
  },
};

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('TODAS');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Minhas Encomendas');
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ecommerceService.getOrders();
      setOrders(data);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(t('orders.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders =
    activeTab === 'TODAS'
      ? orders
      : orders.filter((o) => o.ESTADO === activeTab);

  const toggleExpand = (orderId: string) => {
    setExpandedOrder((prev) => (prev === orderId ? null : orderId));
  };

  const getOrderNumber = (id: string): string => {
    const short = id.replace(/\D/g, '').slice(-4).padStart(4, '0');
    return `#PG-${short}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoaderComponent size="lg" text={t('orders.loadingOrders')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <EmptyState
          icon={<FiPackage className="text-4xl" />}
          title={t('orders.errorTitle')}
          description={error}
          action={
            <button onClick={loadOrders} className="btn-primary">
              {t('common.tryAgain')}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      {/* Header */}
      <h1 className="text-xl font-bold text-pagali-gray-900 mb-6">
        {t('orders.title')}
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-pagali-purple text-white'
                : 'bg-pagali-gray-100 text-pagali-gray-600 hover:bg-pagali-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <EmptyState
          icon={<FiPackage className="text-4xl" />}
          title={t('orders.noOrders')}
          description={
            activeTab === 'TODAS'
              ? t('orders.noOrdersYet')
              : t('orders.noOrdersWithState', { state: TABS.find((tab) => tab.key === activeTab)?.label })
          }
          action={
            activeTab === 'TODAS' ? (
              <button
                onClick={() => navigate('/ecommerce')}
                className="btn-primary"
              >
                {t('checkout.exploreStores')}
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const statusConfig = STATUS_CONFIG[order.ESTADO];
            const isExpanded = expandedOrder === order.ID;

            return (
              <div
                key={order.ID}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                {/* Order Header */}
                <button
                  onClick={() => toggleExpand(order.ID)}
                  className="w-full p-4 flex items-center justify-between"
                >
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-pagali-gray-800 text-sm">
                        {getOrderNumber(order.ID)}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
                      >
                        {statusConfig.icon}
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-pagali-gray-500">
                        {formatDate(order.DATA)}
                      </span>
                      <span className="text-sm font-semibold text-pagali-gray-900">
                        {formatCurrency(order.TOTAL)}
                      </span>
                    </div>
                  </div>
                  {isExpanded ? (
                    <FiChevronUp className="text-pagali-gray-400 flex-shrink-0" />
                  ) : (
                    <FiChevronDown className="text-pagali-gray-400 flex-shrink-0" />
                  )}
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-pagali-gray-100">
                    <div className="pt-3 space-y-2">
                      {order.ITEMS.map((item) => {
                        const imageSrc =
                          item.produto?.IMAGEM && item.produto?.MIME_TYPE
                            ? `data:${item.produto.MIME_TYPE};base64,${item.produto.IMAGEM}`
                            : null;

                        return (
                          <div
                            key={item.ID}
                            className="flex items-center gap-3 py-2"
                          >
                            {/* Item Image */}
                            <div className="w-10 h-10 rounded-lg bg-pagali-gray-100 overflow-hidden flex-shrink-0">
                              {imageSrc ? (
                                <img
                                  src={imageSrc}
                                  alt={item.produto?.NOME || 'Produto'}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FiShoppingBag className="text-sm text-pagali-gray-300" />
                                </div>
                              )}
                            </div>

                            {/* Item Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-pagali-gray-800 truncate">
                                {item.produto?.NOME || `Produto ${item.PRODUTO_ID}`}
                              </p>
                              <p className="text-xs text-pagali-gray-500">
                                {item.QUANTIDADE}x {formatCurrency(item.PRECO_UNITARIO)}
                              </p>
                            </div>

                            {/* Item Subtotal */}
                            <span className="text-sm font-medium text-pagali-gray-900 flex-shrink-0">
                              {formatCurrency(item.SUBTOTAL)}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Order Footer */}
                    <div className="mt-3 pt-3 border-t border-pagali-gray-100">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-pagali-gray-600">
                          {t('common.total')}
                        </span>
                        <span className="text-base font-bold text-pagali-gray-900">
                          {formatCurrency(order.TOTAL)}
                        </span>
                      </div>
                      {order.ENDERECO_ENTREGA && (
                        <p className="text-xs text-pagali-gray-500 mt-2">
                          {t('orders.deliveryAddress')}: {order.ENDERECO_ENTREGA}
                        </p>
                      )}
                      {order.OBSERVACOES && (
                        <p className="text-xs text-pagali-gray-500 mt-1">
                          {t('orders.orderNotes')}: {order.OBSERVACOES}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
